
import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authRoutes } from '@/routes/authRoutes';
import { leadRoutes } from '@/routes/leadRoutes';
import { customerRoutes } from '@/routes/customerRoutes';
import { contactRoutes } from '@/routes/contactRoutes';
import { dashboardRoutes } from '@/routes/dashboardRoutes';
import { communicationRoutes } from '@/routes/communicationRoutes';
import { agentRoutes } from '@/routes/agentRoutes';
import { campaignRoutes } from '@/routes/campaignRoutes';
import { systemRoutes } from '@/routes/systemRoutes';
import { ivrRoutes } from '@/routes/ivrRoutes';
import { profileRoutes } from '@/routes/profileRoutes';
import { reportRoutes } from '@/routes/reportRoutes';
import { serviceRoutes } from '@/routes/serviceRoutes';
import { proposalRoutes } from '@/routes/proposalRoutes';
import AppLayout from '@/components/layout/AppLayout';
import { RouteGuard } from '@/components/auth/RouteGuard';
import { deriveResourceIdFromPath } from '@/services/auth/permissionResolver';
import { logger } from '@/utils/logger';

const LoadingFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="flex flex-col items-center space-y-4">
      <div className="h-8 w-8 rounded-full border-2 border-muted-foreground/20 border-t-primary animate-spin" />
    </div>
  </div>
);

const renderRouteWithLayout = (route: any) => {
  logger.debug('🛣️ Rendering route', {
    path: route.path,
    requiresLayout: route.requiresLayout,
    requiredRoles: route.requiredRoles,
    requiredPermissions: route.requiredPermissions
  });
  const resource = deriveResourceIdFromPath(route.path);
  const accessContext = resource ? { resource } : undefined;

  return (
    <Route
      key={route.path}
      path={route.path}
      element={
        route.requiresLayout !== false ? (
          <AppLayout>
            <RouteGuard requiredRoles={route.requiredRoles || []} requiredPermissions={route.requiredPermissions || []} accessContext={accessContext}>
              <route.element />
            </RouteGuard>
          </AppLayout>
        ) : (
          <RouteGuard requiredRoles={route.requiredRoles || []} requiredPermissions={route.requiredPermissions || []} accessContext={accessContext}>
            <route.element />
          </RouteGuard>
        )
      }
    />
  );
};

export const AppRoutes: React.FC = () => {
  const { user, isLoading } = useAuth();

  logger.debug('🚀 AppRoutes render', { 
    user: !!user, 
    isLoading, 
    userRole: user?.role,
    isSuperuser: user?.isSuperuser,
    currentPath: window.location.pathname
  });

  if (isLoading) {
    logger.debug('⏳ Showing loading fallback');
    return <LoadingFallback />;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Root route handler */}
        <Route 
          path="/" 
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        
        {/* Login route - redirect authenticated users to dashboard */}
        <Route 
          path="/login" 
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              (() => {
                const LoginComponent = authRoutes.find(route => route.path === "/login")?.element;
                return LoginComponent ? <LoginComponent /> : <div>Login component not found</div>;
              })()
            )
          } 
        />
        
        {user ? (
          // Authenticated routes - wrapped in AppLayout
          <>
            {dashboardRoutes.map(renderRouteWithLayout)}
            {leadRoutes.map(renderRouteWithLayout)}
            {customerRoutes.map(renderRouteWithLayout)}
            {contactRoutes.map(renderRouteWithLayout)}
            {communicationRoutes.map(renderRouteWithLayout)}
            {campaignRoutes.map(renderRouteWithLayout)}
            {ivrRoutes.map(renderRouteWithLayout)}
            {agentRoutes.map(renderRouteWithLayout)}
            {reportRoutes.map(renderRouteWithLayout)}
            {systemRoutes.map(renderRouteWithLayout)}
            {serviceRoutes.map(renderRouteWithLayout)}
            {proposalRoutes.map(renderRouteWithLayout)}
            {profileRoutes.map(renderRouteWithLayout)}
          </>
        ) : (
          // Unauthenticated routes - no layout wrapper (excluding login which is handled above)
          <>
            {/* Public routes */}
            {authRoutes
              .filter(route => route.path !== "/login" && route.path !== "*")
              .map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={<route.element />}
                />
              ))}

            {/* Redirect protected paths to login to avoid 404 flash */}
            <Route path="/app/*" element={<Navigate to="/login" replace />} />
            <Route path="/dashboard/*" element={<Navigate to="/login" replace />} />

            {/* Fallback 404 for other public unknown routes */}
            {(() => {
              const FallbackComponent = authRoutes.find(route => route.path === "*")?.element;
              return FallbackComponent ? (
                <Route
                  path="*"
                  element={<FallbackComponent />}
                />
              ) : null;
            })()}
          </>
        )}
      </Routes>
    </Suspense>
  );
};
